import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, Eye } from 'lucide-react';
import axios from "axios";
import { SERVER_URL } from '../../../constants';

const FeaturedProducts = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function getProducts() {
      try {
        const response = await axios.get(`${SERVER_URL}/api/v1/product`);
        const { data } = response.data;
        const topDeals = data.filter((product) => product.top === "topFeatured");
        setFeaturedProducts(topDeals);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setIsLoading(false);
      }
    }
    getProducts();
  }, []);

  useEffect(() => {
    if (featuredProducts.length > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % featuredProducts.length);
      }, 5000); // Change every 5 seconds
      return () => clearInterval(interval);
    }
  }, [featuredProducts]);

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % featuredProducts.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? featuredProducts.length - 1 : prevIndex - 1
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!featuredProducts.length) {
    return (
      <div className="text-center text-gray-600 min-h-[400px] flex items-center justify-center">
        No featured products available right now.
      </div>
    );
  }

  const currentProduct = featuredProducts[currentIndex];
  const discount = Math.round(
    ((currentProduct.originalPrice - currentProduct.price) / currentProduct.originalPrice) * 100
  );

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-10 animate-fade-in">
          Featured <span className="text-blue-600">Spotlight</span>
        </h2>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Image Section */}
          <div className="relative group rounded-lg overflow-hidden shadow-md bg-white">
            <img
              src={currentProduct.imgUrl[0] || '/api/placeholder/500/500'}
              alt={currentProduct.name}
              className="w-full h-[400px] lg:h-[500px] object-contain p-6 transition-transform duration-500 group-hover:scale-105"
            />
            {discount > 0 && (
              <span className="absolute top-4 left-4 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                -{discount}% OFF
              </span>
            )}
            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-md opacity-75 hover:opacity-100 transition-opacity duration-300"
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-md opacity-75 hover:opacity-100 transition-opacity duration-300"
            >
              <ChevronRight className="w-6 h-6 text-gray-700" />
            </button>
          </div>

          {/* Details Section */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col justify-between">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 line-clamp-2 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                {currentProduct.name}
              </h3>
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={18}
                    fill={i < Math.round(currentProduct.averageReview || 4) ? "currentColor" : "none"}
                    strokeWidth={2}
                    className="text-yellow-500"
                  />
                ))}
                <span className="ml-2 text-sm text-gray-600">
                  ({currentProduct.averageReview || 0} reviews)
                </span>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-2xl font-bold text-gray-900">
                  ₹{currentProduct.price.toLocaleString()}
                </span>
                {currentProduct.originalPrice && (
                  <span className="text-md text-gray-500 line-through">
                    ₹{currentProduct.originalPrice.toLocaleString()}
                  </span>
                )}
              </div>
              <p className="text-gray-600 text-sm line-clamp-3 mb-6">
                {currentProduct.description || "Explore this top-tier product designed for excellence."}
              </p>
              {currentProduct.features && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Key Features</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {currentProduct.features.slice(0, 4).map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <button
              onClick={() => navigate(`/product/${currentProduct._id}`)}
              className="mt-6 w-full bg-blue-600 text-white py-3 rounded-md text-sm font-semibold flex items-center justify-center gap-2 hover:bg-blue-700 transition-all duration-300"
            >
              <Eye size={18} /> View Details
            </button>
          </div>
        </div>

        {/* Progress Indicators */}
        <div className="flex justify-center mt-6 gap-2">
          {featuredProducts.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-blue-600 w-6' : 'bg-gray-300 hover:bg-gray-400'
              }`}
            ></button>
          ))}
        </div>

        {/* Offer Banner */}
        <div className="mt-8 bg-blue-600 text-white rounded-md py-3 px-4 text-center">
          <p className="text-sm font-medium">
            🎉 Limited Time Offer: Get extra 10% off with code FEATURE10 🎉
          </p>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;
